import type {
  AutomationCategoryResponse,
  AutomationCategoryUsageResponse,
  AutomationTemplateCategoriesResponse,
  CreateAutomationCategoryRequest,
  ListAutomationCategoriesResponse,
  UpdateAutomationCategoryRequest,
} from '@reach/shared-types/automation-category';
import type { ApiResponse } from '../../../lib/api';
import { apiRequest } from '../../../lib/api-utils';
import { API_ENDPOINTS } from '../../../lib/constants';

const ENDPOINTS = API_ENDPOINTS.AUTOMATION_CATEGORIES;

/** The partner's automation categories, for the templates list filter and the category picker. */
export async function listAutomationCategories(): Promise<AutomationCategoryResponse[]> {
  const response = await apiRequest<ApiResponse<ListAutomationCategoriesResponse>>(ENDPOINTS.LIST);
  return response.data.results;
}

/** Creates a partner category, from the category picker. */
export async function createAutomationCategory(
  input: CreateAutomationCategoryRequest,
): Promise<AutomationCategoryResponse> {
  const response = await apiRequest<ApiResponse<AutomationCategoryResponse>>(ENDPOINTS.CREATE, {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return response.data;
}

/** Renames or recolors a partner category everywhere it is used. */
export async function updateAutomationCategory(args: {
  categoryId: string;
  changes: UpdateAutomationCategoryRequest;
}): Promise<AutomationCategoryResponse> {
  const response = await apiRequest<ApiResponse<AutomationCategoryResponse>>(
    ENDPOINTS.UPDATE(args.categoryId),
    { method: 'PATCH', body: JSON.stringify(args.changes) },
  );
  return response.data;
}

/** Deletes a partner category, removing it from every template and tenant automation using it. */
export async function deleteAutomationCategory(categoryId: string): Promise<void> {
  await apiRequest<ApiResponse<AutomationCategoryResponse>>(ENDPOINTS.DELETE(categoryId), {
    method: 'DELETE',
  });
}

/** How many templates, tenant automations, and tenants use a category; shown before deleting. */
export async function getAutomationCategoryUsage(
  categoryId: string,
): Promise<AutomationCategoryUsageResponse> {
  const response = await apiRequest<ApiResponse<AutomationCategoryUsageResponse>>(
    ENDPOINTS.USAGE(categoryId),
  );
  return response.data;
}

/** The categories on one template, for the automation settings dialog. */
export async function getTemplateCategories(templateId: string): Promise<AutomationCategoryResponse[]> {
  const response = await apiRequest<ApiResponse<AutomationTemplateCategoriesResponse>>(
    ENDPOINTS.TEMPLATE_CATEGORIES(templateId),
  );
  return response.data.categories;
}

/** Replaces the categories on a template. */
export async function setTemplateCategories(args: {
  templateId: string;
  categoryIds: string[];
}): Promise<AutomationCategoryResponse[]> {
  const response = await apiRequest<ApiResponse<AutomationTemplateCategoriesResponse>>(
    ENDPOINTS.TEMPLATE_CATEGORIES(args.templateId),
    { method: 'PUT', body: JSON.stringify({ categoryIds: args.categoryIds }) },
  );
  return response.data.categories;
}
